import React, { useEffect, useRef, useState } from 'react';

import { Button, Icon, Input, InputStatus, Pill, Textarea } from '../';
import { textareaToPlainText } from '../Textarea/utils';
import { textareaInitialState } from '../Textarea/utils';
import {
  attributeValueDescription,
  attributeValueErrors,
  attributeValueState,
  clearAttributeValueErrors,
  isEmptyNewAttributeValue,
} from './attributeValue';
import { useGlobalState, useTooltip } from '../../hooks';

import './AttributeValuesForm.css';

/** What each dot on a row means, since a colour on its own does not say. */
const STATE_TITLE = {
  new: 'New — this value will be created when you save',
  modified: 'Modified — this value will be updated when you save',
  deleted: 'Deleted — this value will be removed when you save',
};

/**
 * The Attribute Values of one Dimension, edited in place.
 *
 * A controlled list: it holds no values of its own, reporting every change through `onChange` so
 * that the form owning it can send them with the attribute in a single save. Rows are listed
 * compactly — name and code — and clicking one opens it as a card in its place, which is what keeps
 * a list of a hundred country codes readable while leaving any of them editable.
 *
 * Nothing is written until the attribute is saved, so the list says what will happen when it is: a
 * value is marked as new or as modified, and one on its way out drops to a group of its own rather
 * than disappearing, where it can be put back.
 *
 * @param {object} props
 * @param {object[]} props.values - The staged values, in the order they are shown.
 * @param {object[]} [props.original] - The values as they were read, for marking what has changed.
 * @param {function} props.onChange - Called with the whole list whenever it changes.
 * @param {boolean} [props.loading] - Disables every control while a save is in flight.
 * @param {object} [props.formErrors] - Server-side errors, keyed `attribute_values.<index>.<column>`.
 */
export const AttributeValuesForm = ({
  values = [],
  original = [],
  onChange,
  loading = false,
  formErrors = {},
}) => {
  const [statuses] = useGlobalState('statuses');
  const [settings] = useGlobalState('settings');
  const { showTooltip, hideTooltip } = useTooltip();

  // Which row is open. Only that row's description is a Draft.js editor; see
  // attributeValueDescription above.
  const [editingIndex, setEditingIndex] = useState(null);
  const [filterTerm, setFilterTerm] = useState('');

  // What the filter box searches, chosen from its own dropdown the way a table's filter is. 'Change'
  // is what the dot on each row says -- new, modified or deleted -- so a long list can be narrowed
  // to what a save is about to do to it.
  const [filterFields, setFilterFields] = useState([
    { value: 'attribute_value_name', label: 'Name', checked: true },
    { value: 'attribute_value_code', label: 'Code', checked: true },
    {
      value: 'attribute_value_description',
      label: 'Description',
      checked: true,
    },
    { value: 'status_id', label: 'Status', checked: true },
    {
      value: 'state',
      label: 'Change',
      tooltip: 'Whether the value is new, modified, unchanged or to be deleted',
      checked: true,
    },
  ]);

  /**
   * Ticks and unticks what the filter searches. Takes either one option toggled or the whole
   * selection, which is the pair of shapes the select reports.
   *
   * @param {object|object[]} item
   */
  const handleFilterFieldsChange = item => {
    setFilterFields(previous =>
      previous.map(field => {
        const selected = Array.isArray(item) ? item.find(x => x.value === field.value) : undefined;

        return {
          ...field,
          checked: Array.isArray(item)
            ? selected !== undefined && selected.checked !== false
            : field.value === item.value
              ? !field.checked
              : field.checked,
        };
      })
    );
  };
  const cardRef = useRef(null);

  /**
   * Whether a value is one of the ones the filter is asking for.
   *
   * Name and code only. A description is held as the rich text the API returns, so matching it
   * would mean converting every row's markup on every keystroke to search the words inside it --
   * and the full Values tab on the attribute itself is where a search that thorough belongs.
   *
   * @param {object} value
   * @returns {boolean}
   */
  const _matchesFilter = value => {
    const term = filterTerm.trim().toLowerCase();

    if (term === '') return true;

    return filterFields
      .filter(field => field.checked)
      .some(field => _fieldText(value, field.value).includes(term));
  };

  /**
   * What one field of a value reads as, for the filter to match against.
   *
   * The description is held as the rich text the API returns, so it is the words inside it that are
   * matched rather than the markup around them -- converted here, per row, only while a term is
   * being matched and only when the description is one of the ticked fields.
   *
   * @param {object} value
   * @param {string} field - One of the filter's field values.
   * @returns {string} Lowercased, and empty when the value has nothing there.
   */
  const _fieldText = (value, field) => {
    if (field === 'state') return attributeValueState(value, original);

    if (field === 'status_id') return (statuses?.[value.status_id]?.label || '').toLowerCase();

    if (field === 'attribute_value_description') {
      return (textareaToPlainText(attributeValueDescription(value)) || '').toLowerCase();
    }

    return (value[field] || '').toLowerCase();
  };

  /**
   * The open row's description, converted back to the raw text the API takes.
   *
   * @param {object[]} list
   * @param {boolean} [dropEmptyNew=true] - Whether an added row nobody filled in is dropped. True
   *   for the ways a card closes by itself -- clicking away, opening another row -- where the
   *   addition was abandoned. False when the user closed it with OK, which is a choice to keep what
   *   is there, even if what is there still needs a name.
   * @returns {object[]}
   */
  const _closed = (list, dropEmptyNew = true) => {
    if (editingIndex === null || !list[editingIndex]) return list;

    const next = [...list];
    const row = {
      ...next[editingIndex],
      attribute_value_description: attributeValueDescription(next[editingIndex]),
      isBeingAdded: false,
    };

    // Added, then left alone: there is nothing to keep, and a nameless row that exists only to
    // report an error is worse than no row at all.
    if (dropEmptyNew && isEmptyNewAttributeValue(row)) {
      return next.filter((_, i) => i !== editingIndex);
    }

    next[editingIndex] = row;

    return next;
  };

  /**
   * The list in the order the API returns it: by name, so a value added at the top and named ends
   * up where it belongs rather than where it was typed. Sorted when a card closes rather than while
   * one is open, since a list that reorders under every keystroke cannot be worked in.
   *
   * @param {object[]} list
   * @returns {object[]}
   */
  const _sorted = list =>
    [...list].sort((a, b) =>
      (a.attribute_value_name || '').localeCompare(b.attribute_value_name || '', undefined, {
        sensitivity: 'base',
      })
    );

  /**
   * Opens a row as a card, closing whichever was open. Opening builds the description's editor,
   * closing converts it back.
   *
   * @param {number|null} index - The row to open, or null to close the open one.
   * @param {boolean} [keepEmpty=false] - Keeps an added row nobody filled in, rather than dropping
   *   it. Set when the user closed the card with OK: see _closed.
   */
  const toggleRow = (index, keepEmpty = false) => {
    // Held by identity rather than by position: closing the open card sorts the list, which moves
    // the row being opened somewhere else.
    const target = index === editingIndex ? null : values[index];
    const next = _sorted(_closed(values, !keepEmpty));
    const opening = target ? next.indexOf(target) : null;

    if (opening !== null && opening >= 0) {
      next[opening] = {
        ...next[opening],
        attribute_value_description: textareaInitialState({
          value: next[opening].attribute_value_description,
        }),
      };
    }

    setEditingIndex(opening !== null && opening >= 0 ? opening : null);
    onChange(next);
  };

  // Clicking away from an open card closes it, which is what OK does -- a card left open is only
  // ever an accident, and its work is kept either way. Listened for only while one is open, and
  // re-registered when the values change so that closing writes back the current list rather than
  // the one captured when the card was opened.
  useEffect(() => {
    if (editingIndex === null) return undefined;

    const closeOnOutsideClick = event => {
      // A click on another value's row is that row's own to handle: its click handler closes this
      // card and opens that one in a single step. Closing here on mousedown instead would collapse
      // the card and move the list up under the pointer, so the row the user aimed at is no longer
      // there when the click lands -- and nothing opens.
      if (event.target.closest?.('.attribute-value-row')) return;

      if (cardRef.current && !cardRef.current.contains(event.target)) toggleRow(editingIndex);
    };

    document.addEventListener('mousedown', closeOnOutsideClick);

    return () => document.removeEventListener('mousedown', closeOnOutsideClick);
  });

  /**
   * Writes one property of one row.
   *
   * @param {number} index
   * @param {object} event - A form control's change event, standard or custom.
   */
  const handleInputChange = (index, event) => {
    const name = event.target ? event.target.name : event.name;
    const value = event.target ? event.target.value : event.value;

    // What the server said about this field no longer describes what is in it.
    const submittedIndex = values.slice(0, index).filter(x => !x.isDeleted).length;
    delete formErrors[`attribute_values.${submittedIndex}.${name}`];

    const next = [...values];
    next[index] = { ...next[index], [name]: value };
    onChange(next);
  };

  /**
   * Appends an empty value and opens it.
   */
  const addValue = () => {
    // A new value has nothing in it to match on, so it would be filtered out of sight the moment it
    // was added. The filter is what goes, not the row.
    setFilterTerm('');

    const next = _closed(values);

    clearAttributeValueErrors(formErrors);

    // At the top rather than the end: in a long list the row just added would otherwise be out of
    // sight, below everything the attribute already has.
    next.unshift({
      // Its first card is open. What the buttons offer while this is true is adding the value or
      // abandoning it; once the card has been closed the value exists as far as the list is
      // concerned, and they offer saving it or deleting it instead.
      isBeingAdded: true,
      // A value that has never been saved carries no id; the API inserts the rows that have none.
      attribute_value_id: null,
      attribute_value_name: '',
      attribute_value_code: '',
      attribute_value_description: textareaInitialState({ value: '' }),
      status_id: statuses?.default_glossary_status_id || '',
      isNew: true,
    });

    setEditingIndex(0);
    onChange(next);
  };

  /**
   * Marks a value for deletion, or drops it outright when it was never saved — there is nothing to
   * delete, and nothing to put back, so keeping it on screen would only be confusing.
   *
   * @param {number} index
   */
  const deleteValue = index => {
    const target = values[index];
    const next = _closed(values);

    clearAttributeValueErrors(formErrors);
    setEditingIndex(null);

    // Closing writes the open row back as a new object and, when that row was an empty addition,
    // drops it altogether. Its position is worked out rather than looked up: identity would fail
    // for the one row closing rewrote, which is the row this is most often asked to delete.
    const dropped = next.length !== values.length;

    // The empty addition that closing dropped is the row asked for; there is nothing left to do.
    if (dropped && index === editingIndex) {
      onChange(next);
      return;
    }

    const position = dropped && index > editingIndex ? index - 1 : index;

    onChange(
      target.attribute_value_id
        ? next.map((x, i) => (i === position ? { ...x, isDeleted: true } : x))
        : next.filter((_, i) => i !== position)
    );
  };

  /**
   * Puts a value marked for deletion back.
   *
   * @param {number} index
   */
  const restoreValue = index => {
    clearAttributeValueErrors(formErrors);
    onChange(values.map((x, i) => (i === index ? { ...x, isDeleted: false } : x)));
  };

  // Rendered as two lists, each keeping the index the value has in the staged list so that every
  // handler and every server-side error still addresses the right one.
  // Each row keeps the index it has in the staged list, so filtering changes what is on screen
  // without changing what every handler and every server-side error addresses.
  const indexed = values.map((value, index) => ({ value, index }));
  const kept = indexed.filter(x => !x.value.isDeleted && _matchesFilter(x.value));
  const deleted = indexed.filter(x => x.value.isDeleted && _matchesFilter(x.value));
  const total = values.filter(value => !value.isDeleted).length;

  /**
   * One value's card, open for editing.
   *
   * @param {object} value
   * @param {number} index
   * @param {object} errors
   * @returns {JSX.Element}
   */
  const renderCard = (value, index, errors) => (
    <div className="attribute-value-card" ref={cardRef}>
      <div className="form-block vertical">
        <InputStatus
          name="attribute_value_name"
          label="Name"
          value={value.attribute_value_name}
          statusValue={value.status_id}
          assetCategory="glossary"
          onChange={event => handleInputChange(index, event)}
          loading={loading}
          validation={
            errors.attribute_value_name
              ? { valid: false, message: errors.attribute_value_name }
              : undefined
          }
        />
      </div>

      <div className="form-block vertical">
        <Input
          type="text"
          name="attribute_value_code"
          label="Code"
          value={value.attribute_value_code || ''}
          onChange={event => handleInputChange(index, event)}
          disabled={loading}
          isOptional={true}
          validation={
            errors.attribute_value_code
              ? { valid: false, message: errors.attribute_value_code }
              : undefined
          }
        />
      </div>

      <div className="form-block vertical">
        <Textarea
          name="attribute_value_description"
          value={value.attribute_value_description}
          onChange={event => handleInputChange(index, event)}
          disabled={loading}
          isOptional={true}
        />
      </div>

      <div className="form-block attribute-value-card-actions">
        {/* Closing with this button keeps whatever is in the card, empty or not: it is a decision
            to keep the value, where clicking away is a decision to abandon it. It says what that
            decision is -- adding the value the first time, saving it every time after. */}
        <Button
          value={value.isBeingAdded ? 'Add' : 'Save'}
          buttonStyle="main-button"
          onClick={() => toggleRow(index, true)}
          disabled={loading}
        />
        {/* The same action either way -- it is what the button leaves behind that differs. While a
            value is being added there is nothing yet to delete, only an addition to abandon. */}
        <Button
          value={value.isBeingAdded ? 'Cancel' : 'Delete'}
          onClick={() => deleteValue(index)}
          disabled={loading}
        />
      </div>
    </div>
  );

  /**
   * One value's row, collapsed. The badge sits where an icon would: every value is the same kind of
   * thing, so what is worth showing there is what will happen to this one on save.
   *
   * @param {object} value
   * @param {number} index
   * @param {object} errors
   * @returns {JSX.Element}
   */
  const renderRow = (value, index, errors = {}) => {
    const state = attributeValueState(value, original);
    const description = attributeValueDescription(value);

    return (
      <div
        className={'side-dialog-search-result attribute-value-row' + (loading ? ' disabled' : '')}
        onClick={() => !loading && toggleRow(index)}
      >
        {/* What will happen to this value on save, in the space a list like this usually gives an
            icon -- every attribute value is the same kind of thing, so an icon would say nothing.
            A dot rather than a label: three states, on a row that is mostly name. */}
        <div className="attribute-value-badge">
          {state !== 'unchanged' && (
            <div
              className={`attribute-value-dot ${state}`}
              onMouseEnter={() => showTooltip({ title: STATE_TITLE[state] })}
              onMouseLeave={hideTooltip}
            ></div>
          )}
        </div>

        <div className="side-dialog-search-result-body">
          <div
            className="side-dialog-search-result-title"
            onMouseEnter={() =>
              description &&
              showTooltip({ title: value.attribute_value_name, bodyText: description })
            }
            onMouseLeave={hideTooltip}
          >
            {value.attribute_value_name || (
              <span className="attribute-value-unnamed">Unnamed value</span>
            )}
          </div>
          <div className="side-dialog-search-result-subtitle">{value.attribute_value_code}</div>
        </div>

        <div className="attribute-value-row-actions">
          {/* The same pill the attribute's own Values tab shows, and only where the installation
              uses glossary statuses at all -- where it does not, the card has no status picker
              either. */}
          {settings?.SHOW_GLOSSARY_STATUSES && value.status_id && (
            <Pill label={value.status_id} values="statuses" />
          )}

          {/* Marks a value that cannot be saved as it is, with the icon every validation message in
              the GUI uses, so a problem is visible without opening each row to look for it. */}
          {Object.keys(errors).length > 0 && (
            <div
              className="attribute-value-invalid"
              onMouseEnter={() => showTooltip({ title: 'This value cannot be saved as it is' })}
              onMouseLeave={hideTooltip}
            >
              <Icon name="failed" />
            </div>
          )}

          {value.isDeleted ? (
            <div
              className="attribute-value-restore"
              onMouseEnter={() =>
                showTooltip({ title: 'Keep this value, rather than deleting it when you save' })
              }
              onMouseLeave={hideTooltip}
              onClick={event => {
                event.stopPropagation();
                !loading && restoreValue(index);
              }}
            >
              Restore
            </div>
          ) : (
            <div
              className="icon-delete"
              onMouseEnter={() => showTooltip({ title: 'Delete this value' })}
              onMouseLeave={hideTooltip}
              onClick={event => {
                event.stopPropagation();
                !loading && deleteValue(index);
              }}
            ></div>
          )}
        </div>
      </div>
    );
  };

  return (
    <React.Fragment>
      <div className="form-block vertical">
        <div className="attribute-values-header">
          <Button value="+ Add" onClick={addValue} disabled={loading} />

          <Input
            type="text"
            name="attribute_value_filter"
            value={filterTerm}
            // The clear button reports a plain { name, value } rather than a DOM event, as every
            // custom control in here does.
            onChange={event => setFilterTerm(event.target ? event.target.value : event.value)}
            placeholder="Filter Values..."
            label={false}
            icon="filter"
            iconAsButton={true}
            isNullable={true}
            disabled={loading}
            withSelect={true}
            selectProps={[
              {
                name: 'attribute_value_filter_field',
                value: filterFields.filter(field => field.checked),
                tooltip: 'Select what to filter on',
                options: filterFields,
                onChange: handleFilterFieldsChange,
              },
            ]}
          />
        </div>

        {/* What a filtered list is showing, worded as the tables do it, so a short list is never
            mistaken for the whole list. */}
        {filterTerm.trim() !== '' && (
          <label className="form-light-text attribute-values-showing">
            {`Showing ${kept.length} of ${total} values`}
          </label>
        )}

        {kept.map(({ value, index }) => {
          const errors = attributeValueErrors(values, index, formErrors);

          return (
            <div
              className={'attribute-value' + (index === editingIndex ? ' open' : '')}
              key={index}
            >
              {index === editingIndex
                ? renderCard(value, index, errors)
                : renderRow(value, index, errors)}
            </div>
          );
        })}
      </div>

      {deleted.length > 0 && (
        <div className="form-block vertical attribute-values-deleted">
          <label className="form-strong-text">
            {deleted.length} {deleted.length === 1 ? 'Value' : 'Values'} to delete when you save
          </label>

          {deleted.map(({ value, index }) => (
            <div className="attribute-value" key={index}>
              {renderRow(value, index)}
            </div>
          ))}
        </div>
      )}
    </React.Fragment>
  );
};
